import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { subMinutes } from 'date-fns'
import { Alert, Stack, StackProps, Typography, useTheme } from '@mui/material'
import { Bolt, LocalFireDepartment, Power, Speed, Thermostat } from '@mui/icons-material'
import { Aurora, AuroraAssetGroup, AuroraAwhpData, Sensor } from '../types'
import { formatPower, formatPressure, formatTemperature } from '../utils/string'
import { calculateAuroraElectricPower, calculateAuroraHeatOutput } from '../utils/aurora'
import { getAuroraAwhpDataNow } from '../services/aurora'
import { getSensorNow } from '../services/deviceService'
import { createErrorNotification } from '../redux/slices/notifications'
import { useDispatch } from 'react-redux'

export interface AuroraAssetGroupSummaryProps extends Omit<StackProps, 'children'> {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData?: Record<string, AuroraAwhpData>
  sensors?: Record<string, Sensor>
}

export default function AuroraAssetGroupSummary({
  auroraAssetGroup,
  auroraAwhpData: initialAuroraAwhpData,
  sensors: initialSensors,
  ...props
}: AuroraAssetGroupSummaryProps) {
  const dispatch = useDispatch()
  const { t } = useTranslation('aurora')

  const [auroraAwhpData, setAuroraAwhpData] = useState<Record<string, AuroraAwhpData>>(
    initialAuroraAwhpData !== undefined ? initialAuroraAwhpData : {}
  )

  const [sensors, setSensors] = useState<Record<string, Sensor>>(
    initialSensors !== undefined ? initialSensors : {}
  )

  const auroras = useMemo(() => {
    if (auroraAssetGroup?.assets === undefined) {
      return []
    }

    return auroraAssetGroup.assets
      .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
      .map(({ aurora, ...asset }) => ({ ...aurora!, asset }) as Aurora)
  }, [auroraAssetGroup?.assets])

  const loadAuroraAwhpData = async (controller: AbortController) => {
    const auroraAwhpData: Record<string, AuroraAwhpData> = {}

    for (const aurora of auroras) {
      try {
        const auroraAwhpRecord = await getAuroraAwhpDataNow(aurora.id, {
          signal: controller.signal
        })
        auroraAwhpData[aurora.id] = auroraAwhpRecord
      } catch (_error) {
        dispatch(createErrorNotification('failedToLoadStatus'))
      }
    }

    setAuroraAwhpData(auroraAwhpData)
  }

  const loadSensors = async (controller: AbortController) => {
    const sensors: Record<string, Sensor> = {}

    for (const aurora of auroras) {
      if (!aurora.deviceId) {
        continue
      }

      try {
        const sensor = await getSensorNow(aurora.deviceId, { signal: controller.signal })
        sensors[aurora.deviceId] = sensor
      } catch (error) {
        console.error(error)
      }
    }

    setSensors(sensors)
  }

  const masterAurora = useMemo(() => {
    if (auroras.length === 0 || auroraAssetGroup.customData?.masterId === undefined) {
      return null
    }

    return auroras.find((aurora) => aurora.id === auroraAssetGroup.customData.masterId) || null
  }, [auroras, auroraAssetGroup?.customData?.masterId])

  const heatOutput = useMemo(() => {
    const values: number[] = []

    for (const aurora of auroras) {
      if (aurora.id in auroraAwhpData) {
        const value = calculateAuroraHeatOutput(aurora, auroraAwhpData[aurora.id])
        if (value !== null) {
          values.push(value)
        }
      }
    }

    if (values.length === 0) {
      return null
    }

    return values.reduce((sum, value) => sum + value, 0)
  }, [auroras, auroraAwhpData])

  const electricPower = useMemo(() => {
    const electricPowers: number[] = []

    for (const aurora of auroras) {
      if (aurora.id in auroraAwhpData) {
        const awhpData = auroraAwhpData[aurora.id]
        const sensor =
          aurora.deviceId && aurora.deviceId in sensors ? sensors[aurora.deviceId] : null

        const electricPower = calculateAuroraElectricPower(aurora, awhpData, sensor)

        if (electricPower !== null) {
          electricPowers.push(electricPower)
        }
      }
    }

    if (electricPowers.length === 0) {
      return null
    }

    return electricPowers.reduce((sum, electricPower) => sum + electricPower, 0)
  }, [auroras, auroraAwhpData, sensors])

  const runningCount = useMemo(() => {
    let counter = 0
    for (const aurora of auroras) {
      if (aurora.id in auroraAwhpData) {
        const awhpReading = auroraAwhpData[aurora.id]
        if (new Date(awhpReading.time) > subMinutes(new Date(), 3)) {
          counter++
        }
      }
    }
    return counter
  }, [auroras, auroraAwhpData])

  const pressure = useMemo(() => {
    if (masterAurora === null || masterAurora.id in auroraAwhpData === false) {
      return null
    }

    return auroraAwhpData[masterAurora.id].pressure_value
  }, [masterAurora, auroraAwhpData])

  const outdoorTemperature = useMemo(() => {
    if (masterAurora === null || masterAurora.id in auroraAwhpData === false) {
      return null
    }

    return auroraAwhpData[masterAurora.id].temperature_outdoor
  }, [masterAurora, auroraAwhpData])

  const totalCount = auroras.length

  useEffect(() => {
    if (initialAuroraAwhpData !== undefined) {
      setAuroraAwhpData(initialAuroraAwhpData)
    } else {
      const controller = new AbortController()

      loadAuroraAwhpData(controller)

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => loadAuroraAwhpData(controller), ONE_MINUTE)

      return () => {
        clearInterval(interval)
        controller.abort()
      }
    }
  }, [auroras.length, initialAuroraAwhpData])

  useEffect(() => {
    if (initialSensors !== undefined) {
      setSensors(initialSensors)
    } else {
      const controller = new AbortController()

      loadSensors(controller)

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => loadSensors(controller), ONE_MINUTE)

      return () => {
        clearInterval(interval)
        controller.abort()
      }
    }
  }, [auroras.length, initialSensors])

  return (
    <Stack direction="row" flexWrap="wrap" gap={2} {...props}>
      <StatusInfo
        title={t('heatOutput')}
        icon={<LocalFireDepartment />}
        value={formatPower(heatOutput, true, 1)}
      />

      <StatusInfo
        title={t('electricPower')}
        icon={<Bolt />}
        value={formatPower(electricPower, true, 1)}
      />

      <StatusInfo title={t('running')} icon={<Power />} value={`${runningCount}/${totalCount}`} />

      <StatusInfo
        title={t('pressure')}
        icon={<Speed />}
        value={pressure !== null ? formatPressure(pressure, 'bar', 1) : '-'}
      />

      <StatusInfo
        title={t('outdoorTemperature')}
        icon={<Thermostat />}
        value={outdoorTemperature !== null ? formatTemperature(outdoorTemperature) : '-'}
      />
    </Stack>
  )
}

interface StatusInfoProps {
  title: string
  icon: ReactNode
  value: string
}

function StatusInfo({ title, icon, value }: StatusInfoProps) {
  /**
   * The theme hook.
   */
  const theme = useTheme()

  return (
    <Alert
      color="info"
      icon={icon}
      sx={{
        background: theme.palette.background.default,
        width: { xs: '100%', sm: 200 }
      }}
    >
      <Typography fontWeight={500} color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h4" fontWeight={400}>
        {value}
      </Typography>
    </Alert>
  )
}
